import {
  /* webpack-strip-block:removed */
  RequestTimeoutError
} from '@signal/utils/errors'
import { ErrorWithResponseType, FetchHandlerParamsType } from './types'

export const DEFAULT_FETCH_TIMEOUT = 90000

const CORS_OPTIONS = {
  credentials: 'include' as RequestCredentials,
  mode: 'cors' as RequestMode
}

/**
 * this is the standard fetch algorithm for making GETs
 * @param {FetchDataParamsType} - the parameters as defined in the interface
 *   @param {String} url - the URL to fetch from
 *   @param {Unknown} noContentResponse - what should be returned in the event of no content (204)
 *   @param {FetcherType} fetcher - the fetcher to use (either window.fetch or middleware wrapping it)
 *   @param {Number} fetchTimeout - the fetch timeout to use
 *   @param {Object} options - additional options for the fetcher
 * @return {Promise} - the data fetching promise as returned from window.fetch
 */
export async function fetchData<FetchDataType>({
  url,
  noContentResponse = {},
  fetcher = window.fetch,
  fetchTimeout = DEFAULT_FETCH_TIMEOUT,
  options = {},
  useCredentials = true
}: FetchHandlerParamsType): Promise<FetchDataType> {
  if (options.body) {
    options.headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  }
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, fetchTimeout)

  let response: Response
  try {
    response = await fetcher(url, {
      signal: controller.signal,
      ...(useCredentials ? CORS_OPTIONS : {}),
      ...options
    })
  } catch (err) {
    if (err instanceof DOMException) {
      throw new Error('The operation timed out.  Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }

  try {
    const headers: Record<string, string> = !!options.headers
      ? Object.entries(options.headers as { string: string }).reduce(
          (acc, [key, value]) => ({ ...acc, [key.toLowerCase()]: value }),
          {}
        )
      : {}
    const contentType = headers['content-type'] || ''
    const outputsCsv = contentType && contentType.indexOf('text/csv') !== -1

    let ret
    let rawText: string | undefined
    try {
      if (response.status === 204) {
        ret = noContentResponse
      } else if (outputsCsv) {
        ret = await response.blob()
      } else {
        rawText = await response.text()
        try {
          ret = JSON.parse(rawText)
        } catch {
          // response body is not JSON (e.g. plain-text error from API)
          ret = rawText
        }
      }
    } catch (e) {
      if (response.status === 204) {
        ret = noContentResponse
      } else {
        ret = response
      }
    }
    if (!response.ok) {
      const { status } = response

      let errorText: string
      if (outputsCsv) {
        // server-generated error with response
        errorText = `error (${status}) on url ${url}`
      } else {
        // server-generated error with response — prefer parsed JSON error,
        // then raw text body, then fall back to status code
        const bodyMessage =
          ret?.error?.message ||
          (typeof ret === 'string' && ret ? ret : undefined)
        errorText = `${bodyMessage || status} error (${status}) on url ${url}`
      }

      const contextError = new Error(errorText) as ErrorWithResponseType
      contextError.response = response
      contextError.url = url
      if (rawText !== undefined) {
        contextError.responseText = rawText
      }

      return Promise.reject(contextError)
    }

    return ret
  } catch (err) {
    if (!response.ok) {
      const { status, statusText } = response

      console.log('[swr] fetchError', err, status)

      let error
      switch (status) {
        /* webpack-strip-block:removed */
        case 408:
          error = new RequestTimeoutError()
          break
        default:
          const errorText = `${
            statusText ? statusText : status
          } error (${status})`
          error = new Error(errorText)
      }

      const contextError = error as ErrorWithResponseType
      contextError.response = response
      contextError.url = url
      return Promise.reject(contextError)
    } else if (response.status === 204) {
      return (Promise.resolve(noContentResponse) as unknown) as FetchDataType
    }
    return Promise.reject(err)
  }
}

/**
 * this is the a parallel version of fetchData to use when you don't actually want to fetch (but have to because of
 * the rules of hooks)
 * @param {FetchDataParamsType} - the parameters as defined in the interface
 * @return {Promise} - the data fetching promise (resolved immediately to undefined)
 */
export function fetchDataNull<FetchDataType>(
  fakeResponse: unknown
): (params: FetchHandlerParamsType) => Promise<FetchDataType> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (params: FetchHandlerParamsType): Promise<FetchDataType> => {
    return Promise.resolve(fakeResponse as FetchDataType)
  }
}
